import React from 'react';
import { NumberVisualization } from './NumberVisualization';
import { PieChartVisualization } from './PieChartVisualization';
import { BarChartVisualization } from './BarChartVisualization';
import { TableVisualization } from './TableVisualization';
import { ListVisualization } from './ListVisualization';
import { PercentageVisualization } from './PercentageVisualization';
import { LeaderboardVisualization } from './LeaderboardVisualization';
import type { DynamicWidgetConfig, QueryResult, VisualizationType } from '@/types/dashboard';

/**
 * Click event data passed from visualizations (DASH-24.2)
 */
export interface VisualizationClickEvent {
  /** The clicked value (label for charts, ID for tables/lists) */
  value: string | number | null;
  /** The index of the clicked segment (for charts) */
  index: number | null;
  /** Additional data from the clicked element */
  data?: Record<string, any>;
}

interface DynamicVisualizationProps {
  type: VisualizationType;
  data: QueryResult;
  config: DynamicWidgetConfig;
  /** Callback when a segment/row/item is clicked (DASH-24.2) */
  onSegmentClick?: (event: VisualizationClickEvent) => void;
}

/**
 * Dynamic Visualization Renderer (DASH-8.2, DASH-24.2)
 * Routes to appropriate visualization component based on type
 * Passes click handlers for drill-down functionality
 */
export function DynamicVisualization({ type, data, config, onSegmentClick }: DynamicVisualizationProps) {
  switch (type) {
    case 'number':
      return <NumberVisualization data={data} config={config} onSegmentClick={onSegmentClick} />;
    case 'pie':
      return <PieChartVisualization data={data} config={config} onSegmentClick={onSegmentClick} />;
    case 'bar':
      return <BarChartVisualization data={data} config={config} onSegmentClick={onSegmentClick} />;
    case 'table':
      return <TableVisualization data={data} config={config} onSegmentClick={onSegmentClick} />;
    case 'list':
      return <ListVisualization data={data} config={config} onSegmentClick={onSegmentClick} />;
    case 'percentage':
      return <PercentageVisualization data={data} config={config} onSegmentClick={onSegmentClick} />;
    case 'leaderboard':
      return <LeaderboardVisualization data={data} config={config} onSegmentClick={onSegmentClick} />;
    default:
      return (
        <div className="flex items-center justify-center h-full text-muted-foreground">
          Unknown visualization type: {type}
        </div>
      );
  }
}
